import React, { useState, useMemo, useEffect } from 'react';
import classnames from 'classnames';
import __ from 'utils/i18n';
import Loader from 'components/Loader';
import { ChargesTable } from './components/ChargesTable';
import { ChargeDetailPanel } from './components/ChargeDetailPanel';
import { ChargesReportWidget } from './components/ChargesReportWidget';
import DashboardPanel from 'containers/Dashboard/components/DashboardPanel';
import ServerSideSearch from 'components/ServerSideSearch';
import SelectInput from 'components/forms/inputs/SelectInput';
import { Button } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { fetchResourceRP } from '../_shared/fetchResourceRP';
import { getCharges, getChargeById } from './_shared/resources';
import { getPlans } from '../Plans/_shared/resources';
import { notification } from 'utils/notifications';
import { BackendRpErrorResponse } from '../_shared/BackendRpErrorResponse';
import gtmEvents from 'utils/gtmEvents';

export const ChargesTab = () => {
  const { token } = useSelector((state) => state.api);
  const merchantId = useSelector((state) => state.login.user?.merchantId);

  useEffect(() => {
    gtmEvents.recurringPaymentsPageView('charges', merchantId);
  }, [merchantId]);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [queryParams, setQueryParams] = useState({});
  
  const [showLoader, setShowLoader] = useState(true);
  const isFirstRender = React.useRef(true);
  
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  
  const [showDetails, setShowDetails] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ['recurringPayments', 'charges', currentPage, pageSize, queryParams],
    queryFn: () => {
      const params = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        ...queryParams,
      };
      return fetchResourceRP(getCharges(token), params);
    },
    enabled: !!token,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      setShowLoader(false);
      console.error('[ChargesTab] Error loading charges:', error);
      notification.error(BackendRpErrorResponse.firstErrorMessage(error));
    },
  });

  const { data: plansData } = useQuery({
    queryKey: ['recurringPayments', 'plansForChargesFilter'],
    queryFn: () => fetchResourceRP(getPlans(token), { limit: 100, offset: 0 }),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      console.error('[ChargesTab] Error loading plans:', error);
      notification.error(BackendRpErrorResponse.firstErrorMessage(error));
    },
  });

  const plansOptions = useMemo(() => {
    if (!plansData?.data) return [];
    return plansData.data.map(plan => ({ value: plan.planId || plan.id, label: plan.name }));
  }, [plansData]);

  React.useEffect(() => {
    const params = {};

    if (paymentMethodFilter) params.payment_method_brand = paymentMethodFilter;
    if (statusFilter) params.status = statusFilter;
    if (planFilter) params.plan_id = planFilter;
    if (periodFilter) {
      const now = new Date();
      if (periodFilter === 'lastDay') {
        params.date_from = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      } else if (periodFilter === 'last7Days') {
        params.date_from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      } else if (periodFilter === 'last30Days') {
        params.date_from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      }
      params.date_to = now.toISOString();
    }
    if (searchText) params.search = searchText;

    setQueryParams(params);

    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      setCurrentPage(1);
      setShowLoader(true);
    }
  }, [paymentMethodFilter, statusFilter, planFilter, periodFilter, searchText]);

  const clearFilters = () => {
    setPaymentMethodFilter('');
    setStatusFilter('');
    setPlanFilter('');
    setPeriodFilter('');
  };

  const hasActiveFilters = paymentMethodFilter || statusFilter || planFilter || periodFilter || searchText;

  React.useEffect(() => {
    if (data?.data) {
      setItems(data.data);
      setShowLoader(false);
    }
  }, [data]);

  const handleRowClick = async (charge) => {
    if (!charge) return;
    
    setSelectedCharge(charge);
    setShowDetails(true);
    
    // Si tiene chargeId, intentar cargar el detalle con attempts
    if (charge.chargeId) {
      setLoadingDetail(true);
      try {
        const chargeDetail = await fetchResourceRP(getChargeById(token), charge.chargeId);
        if (chargeDetail) {
          setSelectedCharge(chargeDetail);
        }
      } catch (error) {
        console.error('Error fetching charge detail:', error);
        notification.error(BackendRpErrorResponse.firstErrorMessage(error));
      } finally {
        setLoadingDetail(false);
      }
    }
  };
  
  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedCharge(null);
  };

  const totalPages = data?.pagination ? Math.ceil(data.pagination.total_count / pageSize) : 1;

  return (
    <div
      className={classnames({
        'dashboard-card': true,
        'panel-expanded': showDetails,
      })}
    >
      <DashboardPanel
        panelHeading={__('dashboard.sections.recurringPayments.charges.detail.title')}
        expanded={showDetails}
        hidePanel={handleCloseDetails}
      >
        {showDetails && selectedCharge && (
          <ChargeDetailPanel
            charge={selectedCharge}
            onClose={handleCloseDetails}
            loadingAttempts={loadingDetail}
          />
        )}
      </DashboardPanel>

      <div className="dashboard-card-header recurring-payments-card-header" >
        {!isLoading && (
          <div className="d-flex align-items-top flex-wrap" style={{ gap: '10px' }}>
            <ServerSideSearch
              value={searchText}
              onChange={setSearchText}
              minLength={3}
              maxLength={100}
            />
            <div style={{ width: '180px' }}>
              <SelectInput
                options={[
                  { value: '', label: __('dashboard.sections.recurringPayments.charges.filters.period.label') },
                  { value: 'lastDay', label: __('dashboard.sections.recurringPayments.charges.filters.period.lastDay') },
                  { value: 'last7Days', label: __('dashboard.sections.recurringPayments.charges.filters.period.last7Days') },
                  { value: 'last30Days', label: __('dashboard.sections.recurringPayments.charges.filters.period.last30Days') }
                ]}
                value={periodFilter}
                onChange={(value) => setPeriodFilter(value || '')}
                placeholder={__('dashboard.sections.recurringPayments.charges.filters.period.label')}
                isSearchable={false}
              />
            </div>
            <div style={{ width: '200px' }}>
              <SelectInput
                options={[
                  { value: '', label: __('dashboard.sections.recurringPayments.charges.filters.plan.label') },
                  ...plansOptions
                ]}
                value={planFilter}
                onChange={(value) => setPlanFilter(value || '')}
                placeholder={__('dashboard.sections.recurringPayments.charges.filters.plan.label')}
                isSearchable={false}
              />
            </div>
            <div style={{ width: '180px' }}>
              <SelectInput
                options={[
                  { value: '', label: __('dashboard.sections.recurringPayments.charges.filters.paymentMethod.label') },
                  { value: 'visa', label: 'Visa' },
                  { value: 'mastercard', label: 'Mastercard' },
                  { value: 'amex', label: 'American Express' },
                  { value: 'diners', label: 'Diners Club' },
                  { value: 'cabal', label: 'CABAL' }
                ]}
                value={paymentMethodFilter}
                onChange={(value) => setPaymentMethodFilter(value || '')}
                placeholder={__('dashboard.sections.recurringPayments.charges.filters.paymentMethod.label')}
                isSearchable={false}
              />
            </div>
            <div style={{ width: '180px' }}>
              <SelectInput
                options={[
                  { value: '', label: __('dashboard.sections.recurringPayments.charges.filters.status.label') },
                  { value: 'pending', label: __('dashboard.sections.recurringPayments.charges.table.statuses.pending') },
                  { value: 'processing', label: __('dashboard.sections.recurringPayments.charges.table.statuses.processing') },
                  { value: 'succeeded', label: __('dashboard.sections.recurringPayments.charges.table.statuses.succeeded') },
                  { value: 'failed', label: __('dashboard.sections.recurringPayments.charges.table.statuses.failed') },
                  { value: 'cancelled', label: __('dashboard.sections.recurringPayments.charges.table.statuses.cancelled') },
                  { value: 'pastdue', label: __('dashboard.sections.recurringPayments.charges.table.statuses.pastdue') },
                  { value: 'skippedzeroamount', label: __('dashboard.sections.recurringPayments.charges.table.statuses.skippedzeroamount') },
                  { value: 'skippednullamount', label: __('dashboard.sections.recurringPayments.charges.table.statuses.skippednullamount') },
                ]}
                value={statusFilter}
                onChange={(value) => setStatusFilter(value || '')}
                placeholder={__('dashboard.sections.recurringPayments.charges.filters.status.label')}
                isSearchable={false}
              />
            </div>
            {hasActiveFilters && (
              <Button
                color="link"
                onClick={clearFilters}
                style={{ whiteSpace: 'nowrap' }}
              >
                {__('dashboard.sections.recurringPayments.charges.filters.clearFilters')}
              </Button>
            )}
          </div>
        )}
        <div className="ml-auto d-flex align-items-top">
          {data?.data && data.data.length > 0 && (
            <div className="mr-2">
              <ChargesReportWidget currentFilters={queryParams} />
            </div>
          )}
        </div>
      </div>
      
      <div className="dashboard-card-body">
        {(isLoading || showLoader) && <Loader />}
        {(!items.length && !isLoading && !showLoader) && (
          <div className="dashboard-card-message">
            <div className="d-flex justify-content-center mb-3">
              <div className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: '120px', height: '120px' }}>
                <i className="mdi mdi-cash-multiple" style={{ fontSize: '60px', color: '#999' }} />
              </div>
            </div>
            <h3>{__('dashboard.sections.recurringPayments.charges.noCharges')}</h3>
            <p>{__('dashboard.sections.recurringPayments.charges.noChargesDescription')}</p>
          </div>
        )}
        {(items.length > 0 && !isLoading && !showLoader) && (
          <>
            <ChargesTable
              charges={items}
              onRowClick={handleRowClick}
            />
            
            {data?.pagination && data.pagination.total_count > pageSize && (
              <div className="d-flex justify-content-between align-items-center mt-3 px-3 pb-3">
                <div className="text-muted">
                  {__('dashboard.sections.recurringPayments.charges.showing')} {' '}
                  {Math.min((currentPage - 1) * pageSize + 1, data.pagination.total_count)} - {' '}
                  {Math.min(currentPage * pageSize, data.pagination.total_count)} {' '}
                  {__('dashboard.sections.recurringPayments.charges.of')} {data.pagination.total_count}
                </div>
                <div className="d-flex align-items-center">
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => {
                      setShowLoader(true);
                      setCurrentPage(p => Math.max(1, p - 1));
                    }}
                    className="mr-2 d-flex align-items-center justify-content-center"
                    style={{ minWidth: '32px', minHeight: '32px' }}
                  >
                    <i className="mdi mdi-chevron-left" style={{ lineHeight: 1 }} />
                  </Button>
                  <span className="mx-2">
                    {__('dashboard.sections.recurringPayments.subscribers.page')} {currentPage} {' '}
                    {__('dashboard.sections.recurringPayments.subscribers.of')} {' '}
                    {Math.ceil(data.pagination.total_count / pageSize)}
                  </span>
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    disabled={currentPage >= totalPages || isFetching}
                    onClick={() => {
                      setShowLoader(true);
                      setCurrentPage(p => p + 1);
                    }}
                    className="ml-2 d-flex align-items-center justify-content-center"
                    style={{ minWidth: '32px', minHeight: '32px' }}
                  >
                    <i className="mdi mdi-chevron-right" style={{ lineHeight: 1 }} />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
